// ListBlockEditor — bulleted or numbered list of plain-text items.
//
// Enter on a non-empty item: insert a new item below.
// Backspace on an empty item: remove that item (and merge focus up).

import type { ListBlock, Block } from "../../../lib/blocks";
import { useRef } from "react";
import { t } from "../theme";

export interface ListBlockEditorProps {
  block: ListBlock;
  onChange: (patch: Partial<Block>) => void;
  isSelected: boolean;
}

export function ListBlockEditor({
  block,
  onChange,
  isSelected,
}: ListBlockEditorProps) {
  const refs = useRef<Array<HTMLInputElement | null>>([]);

  function update(idx: number, value: string) {
    const items = [...block.items];
    items[idx] = value;
    onChange({ items } as Partial<ListBlock>);
  }

  function insertAt(idx: number) {
    const items = [...block.items];
    items.splice(idx, 0, "");
    onChange({ items } as Partial<ListBlock>);
    setTimeout(() => refs.current[idx]?.focus(), 0);
  }

  function removeAt(idx: number) {
    if (block.items.length <= 1) return;
    const items = block.items.filter((_, i) => i !== idx);
    onChange({ items } as Partial<ListBlock>);
    setTimeout(() => refs.current[Math.max(0, idx - 1)]?.focus(), 0);
  }

  return (
    <div>
      {isSelected && (
        <div style={{ display: "flex", gap: 6, marginBottom: 8, fontFamily: t.sans }}>
          <button
            type="button"
            onClick={() => onChange({ ordered: false } as Partial<ListBlock>)}
            style={pillStyle(!block.ordered)}
          >
            Bulleted
          </button>
          <button
            type="button"
            onClick={() => onChange({ ordered: true } as Partial<ListBlock>)}
            style={pillStyle(block.ordered)}
          >
            Numbered
          </button>
        </div>
      )}
      <div>
        {block.items.map((item, idx) => (
          <div
            key={idx}
            style={{
              display: "flex",
              alignItems: "baseline",
              gap: 8,
              marginBottom: 4,
            }}
          >
            <span
              style={{
                fontFamily: t.serif,
                fontSize: 16,
                color: t.text3,
                minWidth: block.ordered ? 22 : 12,
                textAlign: "right",
              }}
            >
              {block.ordered ? `${idx + 1}.` : "•"}
            </span>
            <input
              ref={(el) => {
                refs.current[idx] = el;
              }}
              type="text"
              value={item}
              onChange={(e) => update(idx, e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  insertAt(idx + 1);
                }
                if (e.key === "Backspace" && !item) {
                  e.preventDefault();
                  removeAt(idx);
                }
              }}
              placeholder="List item"
              style={{
                flex: 1,
                background: "transparent",
                border: "none",
                outline: "none",
                fontFamily: t.serif,
                fontSize: 16,
                color: t.text,
                padding: 0,
              }}
            />
          </div>
        ))}
      </div>
      {isSelected && (
        <button
          type="button"
          onClick={() => insertAt(block.items.length)}
          style={{
            marginTop: 4,
            background: "transparent",
            border: "none",
            color: t.text3,
            fontFamily: t.sans,
            fontSize: 11,
            cursor: "pointer",
            padding: "2px 6px",
          }}
        >
          + Add item
        </button>
      )}
    </div>
  );
}

function pillStyle(active: boolean): React.CSSProperties {
  return {
    padding: "3px 8px",
    fontSize: 11,
    borderRadius: 4,
    background: active ? t.accentSoft : "transparent",
    color: active ? t.accent : t.text3,
    border: `1px solid ${active ? t.accentStrong : t.border}`,
    cursor: "pointer",
  };
}
